import { relatedCore, routePaths, topicNavigation } from "../config/siteNavigation";
import { manualTestingClusterPages } from "../content/manualTestingCluster";
import { automationTestingClusterPages } from "../content/automationTestingCluster";
import { apiTestingClusterPages } from "../content/apiTestingCluster";
import { interviewQuestionsClusterPages } from "../content/interviewQuestionsCluster";
import { practicalExampleClusterPages } from "../content/practicalExampleCluster";
import { blogSeoPages } from "../content/blogSeoCluster";
import { seoContentPages } from "../content/seoContentPages";

export const siteUrl = "https://qacoder.in";
export const siteName = "QA Coder";
export const brandTagline = "Practical software testing learning for real QA careers.";
export const defaultImage = `${siteUrl}/logo.png`;

function canonical(path) {
  return `${siteUrl}${path}`;
}

function titleCaseToKeywords(label) {
  const base = label.toLowerCase();
  return `${base}, ${base} tutorial, ${base} guide, QA Coder, software testing`;
}

function articleSchema({ path, title, description, dateModified = "2026-04-11" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    mainEntityOfPage: canonical(path),
    author: {
      "@type": "Organization",
      name: siteName,
    },
    publisher: {
      "@type": "Organization",
      name: siteName,
      logo: {
        "@type": "ImageObject",
        url: defaultImage,
      },
    },
    dateModified,
  };
}

function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonical(item.path),
    })),
  };
}

function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: siteName,
  url: siteUrl,
  logo: defaultImage,
  description: "QA Coder is a practical software testing learning platform for Manual Testing, Automation Testing, API Testing, and QA interview preparation.",
  sameAs: [
    "https://www.linkedin.com/in/qa-coder-708418404/",
    "https://www.youtube.com/@QACoder",
    "https://www.instagram.com/qacoder0/?hl=en",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "qacoder0@gmail.com",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi"],
  },
};

const enrollFaqs = [
  {
    question: "Can I join QA Coder from a non-IT background?",
    answer: "Yes. QA Coder starts with manual testing fundamentals and gradually moves into tools, projects, API testing, SQL, and automation.",
  },
  {
    question: "Does QA Coder provide job support?",
    answer: "QA Coder provides resume guidance, mock interviews, project explanation support, and interview preparation.",
  },
  {
    question: "Can I attend a free demo class first?",
    answer: "Yes. Learners can submit the enquiry form to request a free demo class and course guidance.",
  },
];

const seoConfig = {
  [routePaths.home]: {
    title: "QA Coder | Learn Manual Testing, Automation Testing & API Testing",
    description: "QA Coder helps learners master manual testing, automation testing, API testing, interview preparation, and practical QA skills for real careers.",
    keywords: "QA Coder, software testing course, manual testing, automation testing, API testing",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: siteName,
        url: siteUrl,
        potentialAction: {
          "@type": "SearchAction",
          target: `${siteUrl}/home?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      organizationSchema,
    ],
  },
  [routePaths.manualTesting]: {
    title: "Manual Testing Tutorial for Beginners - QA Coder",
    description: "Learn manual testing, SDLC, STLC, test cases, defect life cycle, Jira, testing types, and interview-ready QA fundamentals.",
    keywords: "manual testing tutorial, manual testing for beginners, test cases, STLC, SDLC, QA testing",
    related: relatedCore,
    updatedDate: "Updated April 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/manual-testing",
        title: "Manual Testing Tutorial for Beginners",
        description: "A beginner-friendly manual testing guide covering SDLC, STLC, test cases, defect life cycle, Jira, and QA fundamentals.",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Manual Testing", path: "/manual-testing" },
      ]),
    ],
  },
  [routePaths.automationTestingTopic]: {
    title: "Automation Testing Guide - Selenium, Playwright and Frameworks",
    description: "Understand automation testing benefits, Selenium, Playwright, frameworks, CI/CD, regression testing, and practical QA automation strategy.",
    keywords: "automation testing, selenium, playwright, test automation framework, QA automation",
    related: relatedCore,
    updatedDate: "Updated April 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/automation-testing-topic",
        title: "Automation Testing Guide",
        description: "A practical automation testing guide covering Selenium, Playwright, frameworks, regression testing, and CI/CD.",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Automation Testing", path: "/automation-testing-topic" },
      ]),
    ],
  },
  [routePaths.automationTesting]: {
    title: "Automation Testing Practice - QA Coder",
    description: "Practice automation testing with live UI components including forms, alerts, frames, widgets, tables, drag and drop, and more.",
    keywords: "automation testing practice, selenium practice, UI automation demo, QA practice site",
    related: relatedCore,
    schema: [
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Automation Practice", path: "/automation-testing" },
      ]),
    ],
  },
  [routePaths.automationTestcases]: {
    title: "Automation Testcase Library for Ecommerce, UI and API Scenarios - QA Coder",
    description: "Explore structured automation testcases for ecommerce, authentication, catalog, checkout, API, regression, and framework design with execution steps and expected results.",
    keywords: "automation testcase library, ecommerce automation test cases, selenium scenarios, playwright scenarios, QA automation examples",
    related: relatedCore,
    updatedDate: "Updated May 16, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: routePaths.automationTestcases,
        title: "Automation Testcase Library for Ecommerce, UI and API Scenarios",
        description: "Structured automation testcases for ecommerce, authentication, catalog, checkout, API, regression, and framework design.",
        dateModified: "2026-05-16",
      }),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Automation Testcase Library", path: routePaths.automationTestcases },
      ]),
    ],
  },
  [routePaths.apiTesting]: {
    title: "API Testing Tutorial with Real-Time Scenarios - QA Coder",
    description: "Learn API testing basics, HTTP methods, status codes, Postman, authentication, request-response validation, and real-time scenarios.",
    keywords: "API testing, Postman, Rest Assured, HTTP status codes, API testing tutorial",
    related: relatedCore,
    updatedDate: "Updated April 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/api-testing",
        title: "API Testing Tutorial with Real-Time Scenarios",
        description: "API testing guide covering HTTP methods, status codes, Postman, authentication, request-response validation, and real-time examples.",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "API Testing", path: "/api-testing" },
      ]),
    ],
  },
  [routePaths.softwareTesting]: {
    title: "Software Testing Complete Guide - QA Coder",
    description: "Complete software testing guide covering testing fundamentals, principles, types, levels, techniques, STLC, and testing artifacts.",
    keywords: "software testing, software testing guide, testing types, STLC, QA fundamentals",
    related: relatedCore,
    updatedDate: "Updated April 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/software-testing",
        title: "Software Testing Complete Guide",
        description: "Complete software testing guide covering fundamentals, principles, types, levels, techniques, STLC, and artifacts.",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Software Testing", path: "/software-testing" },
      ]),
    ],
  },
  [routePaths.interviewQuestions]: {
    title: "Software Testing Interview Questions and Answers - QA Coder",
    description: "Prepare for QA interviews with manual testing, automation testing, API testing, defect life cycle, SQL, and scenario-based questions.",
    keywords: "software testing interview questions, QA interview questions, manual testing interview, automation testing interview",
    related: relatedCore,
    updatedDate: "Updated April 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/testing-interview-questions",
        title: "Software Testing Interview Questions and Answers",
        description: "QA interview preparation guide with manual testing, automation testing, API testing, SQL, and scenario-based questions.",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Interview Questions", path: "/testing-interview-questions" },
      ]),
    ],
  },
  [routePaths.studyNotes]: {
    title: "Software Testing Study Notes and Cheat Sheets - QA Coder",
    description: "Quick software testing study notes, cheat sheets, revision topics, QA interview preparation notes, and testing reference material.",
    keywords: "software testing notes, QA study notes, testing cheat sheet, manual testing notes",
    related: relatedCore,
    updatedDate: "Updated April 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/study-notes",
        title: "Software Testing Study Notes and Cheat Sheets",
        description: "Quick QA study notes and testing cheat sheets for revision and interview preparation.",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Study Notes", path: "/study-notes" },
      ]),
    ],
  },
  [routePaths.premiumNotes]: {
    title: "Premium QA PDFs and eBooks - QA Coder",
    description: "Browse paid QA PDF packs and eBooks for JIRA, API testing, manual testing, Selenium, SQL, interview prep, and real project documents.",
    keywords: "QA PDF notes, JIRA PDF, API testing ebook, paid QA notes, software testing ebooks",
    related: relatedCore,
    updatedDate: "Updated May 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: routePaths.premiumNotes,
        title: "Premium QA PDFs and eBooks",
        description: "Paid QA PDF packs and eBooks for JIRA, API testing, manual testing, Selenium, SQL, interview prep, and project documents.",
        dateModified: "2026-05-11",
      }),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Premium PDFs", path: routePaths.premiumNotes },
      ]),
    ],
  },
  [routePaths.jiraNotes]: {
    title: "JIRA QA Notes Pack for Rs. 99 - QA Coder",
    description: "Premium JIRA QA notes pack with roadmap, workflows, JQL, dashboards, reports, bug lifecycle, and interview answers for testers.",
    keywords: "JIRA notes PDF, JIRA for QA, JQL notes, JIRA interview questions, bug lifecycle in JIRA",
    related: relatedCore,
    updatedDate: "Updated April 28, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: routePaths.jiraNotes,
        title: "JIRA QA Notes Pack for Rs. 99",
        description: "Premium JIRA notes pack with QA workflows, JQL, dashboards, reports, bug lifecycle, and interview answers.",
        dateModified: "2026-04-28",
      }),
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "JIRA QA Notes Pack",
        description: "Premium JIRA learning notes for QA learners with roadmap, interview questions, and practical examples.",
        brand: {
          "@type": "Brand",
          name: siteName,
        },
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          price: "99",
          availability: "https://schema.org/InStock",
          url: canonical(routePaths.jiraNotes),
        },
      },
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Study Notes", path: routePaths.studyNotes },
        { name: "JIRA Notes Pack", path: routePaths.jiraNotes },
      ]),
    ],
  },
  [routePaths.jiraNotesCheckout]: {
    title: "Buy JIRA QA Notes Pack - QA Coder",
    description: "Premium checkout for the QA Coder JIRA notes pack with Rs. 99 access, full notes unlock, and printable PDF-style study material.",
    keywords: "buy JIRA notes, JIRA QA notes checkout, premium QA notes, JIRA PDF notes",
    noindex: true,
  },
  [routePaths.jiraNotesDownload]: {
    title: "JIRA QA Notes Premium Download - QA Coder",
    description: "Premium printable JIRA QA notes with workflow, JQL, dashboards, bug lifecycle, and enterprise interview answers.",
    keywords: "JIRA notes download, premium JIRA PDF, QA Coder JIRA notes, JQL PDF notes",
    noindex: true,
  },
  [routePaths.testingBlog]: {
    title: "Software Testing Blog - QA Coder",
    description: "Read software testing articles, QA tutorials, automation guides, testing career tips, and interview preparation content.",
    keywords: "software testing blog, QA blog, automation testing articles, testing career",
    related: relatedCore,
    updatedDate: "Updated April 11, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/testing-blog",
        title: "Software Testing Blog",
        description: "Software testing articles, QA tutorials, automation guides, testing career tips, and interview preparation content.",
      }),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Testing Blog", path: "/testing-blog" },
      ]),
    ],
  },
  [routePaths.blogArchive]: {
    title: "Testing Blog Archive - QA Coder",
    description: "Browse the QA Coder testing blog archive with software testing tutorials, QA guides, interview tips, and automation articles.",
    keywords: "testing blog archive, QA blog archive, software testing articles, QA Coder blog",
    related: relatedCore,
    updatedDate: "Updated May 16, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Testing Blog Archive", path: routePaths.blogArchive },
      ]),
    ],
  },
  [routePaths.product]: {
    title: "QA Coder Product Catalog - Ecommerce Practice Store",
    description: "Browse the QA Coder ecommerce practice catalog with product listings, search, flash sales, detail pages, and automation-friendly shopping flows.",
    keywords: "ecommerce practice website, product catalog, ecommerce testing practice, QA Coder store",
    updatedDate: "Updated May 16, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Product Catalog", path: routePaths.product },
      ]),
    ],
  },
  [routePaths.flashSales]: {
    title: "Flash Sale Products - Ecommerce Testing Practice | QA Coder",
    description: "Explore flash sale products on the QA Coder ecommerce practice store for automation testing around listing cards, pricing, navigation, and cart flows.",
    keywords: "flash sale products, ecommerce automation practice, shopping cart testing, QA Coder ecommerce",
    updatedDate: "Updated May 16, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Flash Sales", path: routePaths.flashSales },
      ]),
    ],
  },
  [routePaths.productsByCategory]: {
    title: "Product Category Listing - Ecommerce Testing Practice | QA Coder",
    description: "Category-wise ecommerce product listing page for automation testing of filters, sorting, search relevance, cart actions, and product discovery.",
    keywords: "category listing page, ecommerce category testing, product listing automation, QA Coder ecommerce",
    updatedDate: "Updated May 16, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Product Categories", path: routePaths.productsByCategory },
      ]),
    ],
  },
  [routePaths.productSearch]: {
    title: "Product Search Results - Ecommerce Automation Practice | QA Coder",
    description: "Search product listings on the QA Coder ecommerce practice store and test search relevance, result rendering, cart actions, and product navigation flows.",
    keywords: "product search listing, ecommerce search testing, search result automation, QA Coder product search",
    updatedDate: "Updated May 16, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Product Search", path: routePaths.productSearch },
      ]),
    ],
  },
  [routePaths.cart]: {
    title: "Shopping Cart - QA Coder Ecommerce Practice",
    description: "Review cart items, quantities, pricing, and checkout actions on the QA Coder ecommerce practice store.",
    keywords: "shopping cart, ecommerce cart page, cart testing practice",
    noindex: true,
  },
  [routePaths.address]: {
    title: "Shipping Address - QA Coder Ecommerce Practice",
    description: "Manage shipping address details during checkout on the QA Coder ecommerce practice store.",
    keywords: "shipping address page, checkout address form, ecommerce address testing",
    noindex: true,
  },
  [routePaths.payment]: {
    title: "Payment Method - QA Coder Ecommerce Practice",
    description: "Choose payment method and complete checkout in the QA Coder ecommerce practice environment.",
    keywords: "payment method page, ecommerce payment testing, checkout payment ui",
    noindex: true,
  },
  [routePaths.orderHistory]: {
    title: "Order History - QA Coder Ecommerce Practice",
    description: "Review previously placed orders in the QA Coder ecommerce practice store.",
    keywords: "order history page, ecommerce order history testing",
    noindex: true,
  },
  [routePaths.orderSuccess]: {
    title: "Order Success - QA Coder Ecommerce Practice",
    description: "Order success confirmation page in the QA Coder ecommerce practice store.",
    keywords: "order success page, ecommerce success testing",
    noindex: true,
  },
  [routePaths.login]: {
    title: "Login - QA Coder",
    description: "Login page for QA Coder learners and dashboard access.",
    keywords: "QA Coder login",
    noindex: true,
  },
  [routePaths.signup]: {
    title: "Sign Up - QA Coder",
    description: "Create a QA Coder account to access learning tools, notes, and dashboards.",
    keywords: "QA Coder signup, register QA Coder",
    noindex: true,
  },
  "/top-regression-testing-tools": {
    title: "Top Regression Testing Tools and Autonomous Testing Guide - QA Coder",
    description: "Compare Selenium, Playwright, Cypress, Katalon, and AI-assisted autonomous testing tools for regression automation with examples and FAQs.",
    keywords: "top regression testing tools, autonomous testing, Selenium, Playwright, Cypress, Katalon, regression automation",
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: "/top-regression-testing-tools",
        title: "Top Regression Testing Tools and Autonomous Testing Guide",
        description: "A practical guide comparing regression testing tools and explaining autonomous testing for QA learners.",
        dateModified: "2026-04-12",
      }),
      faqSchema([
        {
          question: "Which regression testing tool is best for beginners?",
          answer: "Selenium is a strong beginner choice for QA interviews, while Playwright is excellent for modern reliable automation.",
        },
        {
          question: "Is autonomous testing the same as automation testing?",
          answer: "No. Automation runs scripted tests, while autonomous testing uses AI-assisted capabilities to generate, maintain, or execute tests with less manual scripting.",
        },
      ]),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Automation Testing", path: "/automation-testing-tutorial" },
        { name: "Regression Testing Tools", path: "/top-regression-testing-tools" },
      ]),
    ],
  },
  [routePaths.enroll]: {
    title: "Software Testing Course with Free Demo - QA Coder",
    description: "Join QA Coder's software testing course with Manual Testing, Selenium, Playwright, API Testing, SQL, projects, resume help, and mock interviews.",
    keywords: "software testing course, QA course, manual testing course, automation testing course, Selenium course",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Course",
        name: "Complete QA Career Program",
        description: "Software testing course covering Manual Testing, Selenium, Playwright, API Testing, SQL, projects, resume support, and mock interviews.",
        provider: {
          "@type": "Organization",
          name: siteName,
          sameAs: siteUrl,
        },
      },
      faqSchema(enrollFaqs),
      breadcrumbSchema([
        { name: "Home", path: "/home" },
        { name: "Enroll", path: "/enroll" },
      ]),
    ],
  },
  [routePaths.contact]: {
    title: "Contact QA Coder - Course Enquiry and Support",
    description: "Contact QA Coder for software testing course enquiries, demo class booking, training support, and learning guidance.",
    keywords: "contact QA Coder, software testing course enquiry, QA training support",
  },
  [routePaths.about]: {
    title: "About QA Coder - Practical Software Testing Learning Platform",
    description: "Learn about QA Coder, a practical software testing learning platform for Manual Testing, Automation Testing, API Testing, and interview preparation.",
    keywords: "about QA Coder, software testing trainer, QA learning platform, manual testing automation testing API testing",
    schema: [
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "About QA Coder", path: routePaths.about },
      ]),
    ],
  },
  [routePaths.login]: {
    title: "Login - QA Coder",
    description: "Login to QA Coder to access your dashboard, learning resources, and software testing course account.",
  },
  [routePaths.signup]: {
    title: "Create Account - QA Coder",
    description: "Create your QA Coder account to start learning software testing and access your role-based dashboard.",
  },
  "/privacy-policy": {
    title: "Privacy Policy - QA Coder",
    description: "Read how QA Coder collects, stores, and uses learner, enquiry, account, and analytics information.",
    keywords: "QA Coder privacy policy, data privacy, learner information",
  },
  "/terms": {
    title: "Terms and Conditions - QA Coder",
    description: "Read the terms for using QA Coder learning content, automation practice tools, accounts, and course services.",
    keywords: "QA Coder terms, course terms, website terms",
  },
  "/refund-policy": {
    title: "Refund Policy - QA Coder",
    description: "Read QA Coder refund and batch-transfer guidance for software testing course learners.",
    keywords: "QA Coder refund policy, course refund, batch transfer",
  },
};

const generatedTopicSeo = topicNavigation
  .flatMap((group) => group.items.map((item) => ({ ...item, group: group.group })))
  .reduce((configs, item) => {
    if (seoConfig[item.to]) {
      return configs;
    }

    configs[item.to] = {
      title: `${item.label} Tutorial and Guide - QA Coder`,
      description: `Learn ${item.label} with QA Coder through beginner-friendly explanations, practical examples, related testing topics, and interview-focused guidance.`,
      keywords: titleCaseToKeywords(item.label),
      related: relatedCore,
      updatedDate: "Updated April 12, 2026",
      author: "QA Coder Editorial Team",
      schema: [
        articleSchema({
          path: item.to,
          title: `${item.label} Tutorial and Guide`,
          description: `A practical QA Coder guide for ${item.label}, including testing concepts, examples, and interview-focused learning points.`,
          dateModified: "2026-04-12",
        }),
        breadcrumbSchema([
          { name: "Home", path: routePaths.home },
          { name: item.group, path: routePaths.softwareTesting },
          { name: item.label, path: item.to },
        ]),
      ],
    };

    return configs;
  }, {});

const seoContentPageConfigs = seoContentPages.reduce((configs, page) => {
  configs[page.path] = {
    title: `${page.title} - QA Coder`,
    description: page.description,
    keywords: `${page.keyword}, ${page.keyword.toLowerCase()}, software testing, QA interview preparation, QA Coder`,
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: page.path,
        title: page.title,
        description: page.description,
        dateModified: "2026-04-12",
      }),
      faqSchema(
        page.sections.find((section) => section.faqs)?.faqs || []
      ),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "SEO Learning Guides", path: routePaths.testingBlog },
        { name: page.title, path: page.path },
      ]),
    ],
  };

  return configs;
}, {});

const manualClusterSeoConfigs = manualTestingClusterPages.reduce((configs, page) => {
  const faqSection = page.sections.find((section) => section.faqs);

  configs[page.path] = {
    title: `${page.title} - QA Coder`,
    description: page.description,
    keywords: `${page.keyword}, ${page.keyword.toLowerCase()}, manual testing tutorial, manual testing examples, QA Coder`,
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: page.path,
        title: page.title,
        description: page.description,
        dateModified: "2026-04-12",
      }),
      faqSchema(faqSection?.faqs || []),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Manual Testing", path: routePaths.manualTesting },
        { name: page.keyword, path: page.path },
      ]),
    ],
  };

  return configs;
}, {});

const automationClusterSeoConfigs = automationTestingClusterPages.reduce((configs, page) => {
  const faqSection = page.sections.find((section) => section.faqs);

  configs[page.path] = {
    title: `${page.title} - QA Coder`,
    description: page.description,
    keywords: `${page.keyword}, ${page.keyword.toLowerCase()}, automation testing tutorial, selenium tutorial, QA Coder`,
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: page.path,
        title: page.title,
        description: page.description,
        dateModified: "2026-04-12",
      }),
      faqSchema(faqSection?.faqs || []),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Automation Testing", path: routePaths.automationTesting },
        { name: page.keyword, path: page.path },
      ]),
    ],
  };

  return configs;
}, {});

const apiClusterSeoConfigs = apiTestingClusterPages.reduce((configs, page) => {
  const faqSection = page.sections.find((section) => section.faqs);

  configs[page.path] = {
    title: `${page.title} - QA Coder`,
    description: page.description,
    keywords: `${page.keyword}, ${page.keyword.toLowerCase()}, api testing tutorial, postman tutorial, REST API testing, QA Coder`,
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: page.path,
        title: page.title,
        description: page.description,
        dateModified: "2026-04-12",
      }),
      faqSchema(faqSection?.faqs || []),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "API Testing", path: routePaths.apiTesting },
        { name: page.keyword, path: page.path },
      ]),
    ],
  };

  return configs;
}, {});

const interviewClusterSeoConfigs = interviewQuestionsClusterPages.reduce((configs, page) => {
  const questions = page.sections.flatMap((section) => section.qas).slice(0, 20);

  configs[page.path] = {
    title: `${page.title} - QA Coder`,
    description: page.description,
    keywords: `${page.keyword}, ${page.keyword.toLowerCase()}, QA interview questions, software testing interview questions, QA Coder`,
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: page.path,
        title: page.title,
        description: page.description,
        dateModified: "2026-04-12",
      }),
      faqSchema(
        questions.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))
      ),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Interview Questions", path: routePaths.interviewQuestions },
        { name: page.keyword, path: page.path },
      ]),
    ],
  };

  return configs;
}, {});

const practicalExampleSeoConfigs = practicalExampleClusterPages.reduce((configs, page) => {
  configs[page.path] = {
    title: `${page.title} - QA Coder`,
    description: page.description,
    keywords: `${page.keyword}, ${page.keyword.toLowerCase()}, test case example, QA practical examples, software testing examples`,
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: page.path,
        title: page.title,
        description: page.description,
        dateModified: "2026-04-12",
      }),
      faqSchema(page.faqs),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Practical Examples", path: "/real-time-testing-scenarios" },
        { name: page.keyword, path: page.path },
      ]),
    ],
  };

  return configs;
}, {});

const blogSeoConfigs = blogSeoPages.reduce((configs, page) => {
  configs[page.path] = {
    title: `${page.title} - QA Coder Blog`,
    description: page.description,
    keywords: `${page.keyword}, ${page.keyword.toLowerCase()}, QA blog, software testing blog, QA Coder`,
    related: relatedCore,
    updatedDate: "Updated April 12, 2026",
    author: "QA Coder Editorial Team",
    schema: [
      articleSchema({
        path: page.path,
        title: page.title,
        description: page.description,
        dateModified: "2026-04-12",
      }),
      faqSchema(page.faqs),
      breadcrumbSchema([
        { name: "Home", path: routePaths.home },
        { name: "Testing Blog", path: routePaths.testingBlog },
        { name: page.keyword, path: page.path },
      ]),
    ],
  };

  return configs;
}, {});

export const defaultSeo = {
  title: "QA Coder - Software Testing Learning Platform",
  description: "Learn manual testing, automation testing, API testing, QA interview preparation, and practical software testing skills with QA Coder.",
  keywords: "QA Coder, software testing, manual testing, automation testing, API testing",
  related: relatedCore,
};

export function getSeoConfig(pathname) {
  if (pathname.startsWith("/product-details/")) {
    return {
      title: "Product Details - QA Coder Ecommerce Practice",
      description: "View product details, specifications, price, category, availability, and purchase actions on the QA Coder ecommerce practice store.",
      keywords: "product details page, ecommerce product detail testing, QA Coder ecommerce",
      updatedDate: "Updated May 16, 2026",
      author: "QA Coder Editorial Team",
      schema: [
        breadcrumbSchema([
          { name: "Home", path: routePaths.home },
          { name: "Product Catalog", path: routePaths.product },
          { name: "Product Details", path: pathname },
        ]),
      ],
    };
  }

  if (pathname.startsWith("/invoice/")) {
    return {
      title: "Invoice - QA Coder Ecommerce Practice",
      description: "Transaction invoice page for the QA Coder ecommerce practice store.",
      keywords: "invoice page, ecommerce invoice",
      noindex: true,
    };
  }

  if (pathname.startsWith("/track-order/")) {
    return {
      title: "Track Order - QA Coder Ecommerce Practice",
      description: "Track order status in the QA Coder ecommerce practice store.",
      keywords: "order tracking page, ecommerce order tracking",
      noindex: true,
    };
  }

  return blogSeoConfigs[pathname] || interviewClusterSeoConfigs[pathname] || practicalExampleSeoConfigs[pathname] || seoConfig[pathname] || manualClusterSeoConfigs[pathname] || automationClusterSeoConfigs[pathname] || apiClusterSeoConfigs[pathname] || seoContentPageConfigs[pathname] || generatedTopicSeo[pathname] || defaultSeo;
}

export function getCanonicalUrl(pathname) {
  return canonical(pathname);
}
